import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AiAgentAvatar, Breadcrumb, EmployeeAvatar, LocalizedDateInput, RichTextEditor, Spinner, ActivityIcon, ButtonWithIcon, CommentsIcon, CreateIcon, SaveIcon, actionIconStyles } from '../../../shared/components';
import type { MentionTerm } from '../../../shared/utils/mentionHighlight';
import { decorateMentionsHtml } from '../../../shared/utils/mentionHighlight';
import { TimeInput24 } from '../../../shared/components/TimeInput24';
import { employeeService } from '../../admin/services/employeeService';
import { getContacts } from '../../contacts';
import { getAssets } from '../../assets';
import { useAuth } from '../../auth/context/AuthContext';
import { PermissionCodes } from '../../auth/permissions';
import { VisitPlaceFields } from '../../field-operations/components/VisitPlaceFields';
import type { FieldVisitPlace } from '../../field-operations/types/FieldOperations';
import { emptyVisitPlace } from '../../field-operations/visitPlace';
import {
  ATTACHMENT_ACCEPT,
  PendingAttachment,
  formatBytes,
  revokePendingAttachment,
  revokePendingAttachments,
  selectPendingAttachments,
} from '../../../shared/utils/conversationAttachments';
import { sanitizeRichTextHtml } from '../../../shared/utils/sanitizeRichTextHtml';
import { formatDate, formatDateTime, formatTaskDueDateLabel, formatTimeInputLabel, parseDateInputValue, toDateInputValue } from '../../../shared/utils/localization';
import {
  CreateTaskRequest,
  TaskActivityItem,
  TaskAiOperationItem,
  TaskAttachmentItem,
  TaskCategoryItem,
  TaskCommentItem,
  TaskCommentMentionInput,
  TaskConversationLinkItem,
  TaskLabelItem,
  TaskRelatedTaskLinkItem,
  SubtaskItem,
  TaskItem,
  TaskPriority,
  TaskRecurrence,
  TaskStatusDefinition,
  TaskAiRunStatus,
  TaskStatus,
  UpdateTaskRequest,
  AI_ASSISTANT_ID,
  AI_TASK_ASSIGNEE,
  isAiAssistantId,
} from '../types/Task';
import { taskService, getCachedTaskStatuses } from '../services/taskService';
import { buildCategorySelectOptions, formatCategoryLabel } from '../utils/taskCategoryUtils';
import {
  getAllowedTaskStatuses,
  getBlockedTaskStatusesByAssignee,
  getTaskStatusDefinition,
  getTaskStatusLabel,
  isTaskStatusCompleted,
  sortTaskStatuses,
} from '../utils/taskStatusTransitions';
import { getStatusBadgeStyle } from '../utils/statusManager';
import { taskValidationItems } from '../utils/taskValidationMessage';
import { TaskAiActivityList } from './TaskAiActivityList';
import { TaskAgentUseCaseLink } from './TaskAgentUseCaseLink';
import styles from './TaskEditor.module.css';

type TaskEditorProps = {
  taskId?: string;
  onSaved?: () => void;
  onCancel?: () => void;
  embedded?: boolean;
  initialStatusOptions?: TaskStatusDefinition[];
  initialCategoryOptions?: TaskCategoryItem[];
};

type EditorForm = {
  title: string;
  description: string;
  dueDate: string;
  dueTime: string;
  recurrence: TaskRecurrence;
  priority: TaskPriority;
  status: TaskStatus;
  categoryId: string;
  contactId: string;
  assetId: string;
  assignedToUserId: string;
  assignedToAi: boolean;
  labels: string[];
  visitPlace?: FieldVisitPlace;
};

const defaultForm: EditorForm = {
  title: '',
  description: '',
  dueDate: '',
  dueTime: '',
  recurrence: 'doesNotRepeat',
  priority: 'medium',
  status: '',
  categoryId: '',
  contactId: '',
  assetId: '',
  assignedToUserId: '',
  assignedToAi: false,
  labels: [],
  visitPlace: undefined,
};

const pickDefaultStatusCode = (statuses: TaskStatusDefinition[]): string => {
  const sorted = sortTaskStatuses(statuses);
  return (sorted.find(status => status.isDefault) ?? sorted[0])?.code || '';
};

const pickDefaultCategoryId = (categories: TaskCategoryItem[]): string =>
  categories.find(category => category.name.toLowerCase() === 'general')?.id || categories[0]?.id || '';

const createInitialForm = (
  statusOptions: TaskStatusDefinition[] = [],
  categoryOptions: TaskCategoryItem[] = [],
): EditorForm => ({
  ...defaultForm,
  status: pickDefaultStatusCode(statusOptions),
  categoryId: pickDefaultCategoryId(categoryOptions),
});

const resolveSeedStatusOptions = (initialStatusOptions?: TaskStatusDefinition[]): TaskStatusDefinition[] => {
  if (initialStatusOptions && initialStatusOptions.length > 0) {
    return sortTaskStatuses(initialStatusOptions);
  }

  return sortTaskStatuses(getCachedTaskStatuses());
};

type AttachmentPreviewTone = 'pdf' | 'sheet' | 'doc' | 'slide' | 'archive' | 'text' | 'media' | 'file';

const PDF_EXTENSIONS = new Set(['pdf']);
const SHEET_EXTENSIONS = new Set(['xls', 'xlsx', 'csv']);
const DOC_EXTENSIONS = new Set(['doc', 'docx']);
const SLIDE_EXTENSIONS = new Set(['ppt', 'pptx']);
const ARCHIVE_EXTENSIONS = new Set(['zip', 'rar', '7z', 'tar', 'gz']);
const TEXT_EXTENSIONS = new Set(['txt', 'rtf', 'md']);

const getAttachmentExtension = (fileName: string) => fileName.split('.').pop()?.trim().toLowerCase() ?? '';

const getAttachmentTypeToken = (fileName: string, mimeType: string): { label: string; tone: AttachmentPreviewTone } => {
  const extension = getAttachmentExtension(fileName);
  const normalizedMime = mimeType.toLowerCase();

  if (normalizedMime.startsWith('image/')) return { label: 'IMG', tone: 'media' };
  if (normalizedMime.startsWith('video/')) return { label: 'VID', tone: 'media' };
  if (normalizedMime.startsWith('audio/')) return { label: 'AUD', tone: 'media' };

  if (PDF_EXTENSIONS.has(extension) || normalizedMime.includes('pdf')) {
    return { label: 'PDF', tone: 'pdf' };
  }

  if (SHEET_EXTENSIONS.has(extension) || normalizedMime.includes('excel') || normalizedMime.includes('spreadsheet') || normalizedMime.includes('csv')) {
    return { label: extension ? extension.toUpperCase().slice(0, 4) : 'XLS', tone: 'sheet' };
  }

  if (DOC_EXTENSIONS.has(extension) || normalizedMime.includes('word') || normalizedMime.includes('document')) {
    return { label: extension ? extension.toUpperCase().slice(0, 4) : 'DOC', tone: 'doc' };
  }

  if (SLIDE_EXTENSIONS.has(extension) || normalizedMime.includes('powerpoint') || normalizedMime.includes('presentation')) {
    return { label: extension ? extension.toUpperCase().slice(0, 4) : 'PPT', tone: 'slide' };
  }

  if (ARCHIVE_EXTENSIONS.has(extension) || normalizedMime.includes('zip') || normalizedMime.includes('compressed')) {
    return { label: extension ? extension.toUpperCase().slice(0, 4) : 'ZIP', tone: 'archive' };
  }

  if (TEXT_EXTENSIONS.has(extension) || normalizedMime.startsWith('text/')) {
    return { label: extension ? extension.toUpperCase().slice(0, 4) : 'TXT', tone: 'text' };
  }

  if (extension) {
    return { label: extension.toUpperCase().slice(0, 4), tone: 'file' };
  }

  return { label: 'FILE', tone: 'file' };
};

const toEditorForm = (task: {
  title: string;
  description?: string;
  dueDate?: string;
  dueTime?: string;
  recurrence: TaskRecurrence;
  priority: TaskPriority;
  status: TaskStatus;
  category?: TaskCategoryItem;
  contactId?: string;
  assetId?: string;
  assignedToUserId?: string;
  assignedToAi?: boolean;
  labels?: TaskLabelItem[];
  visitPlace?: FieldVisitPlace;
}): EditorForm => ({
  title: task.title,
  description: task.description || '',
  dueDate: toDateInputValue(task.dueDate),
  dueTime: task.dueTime ? task.dueTime.slice(0, 5) : '',
  recurrence: task.recurrence,
  priority: task.priority,
  status: task.status,
  categoryId: task.category?.id || '',
  contactId: task.contactId || '',
  assetId: task.assetId || '',
  assignedToUserId: task.assignedToAi ? '' : (task.assignedToUserId || ''),
  assignedToAi: Boolean(task.assignedToAi),
  labels: task.labels?.map(label => label.name) || [],
  visitPlace: task.visitPlace,
});

const toUpdateRequest = (form: EditorForm, savedForm?: EditorForm): UpdateTaskRequest => {
  const assigneeChanged = !savedForm
    || form.assignedToAi !== savedForm.assignedToAi
    || form.assignedToUserId !== savedForm.assignedToUserId;

  return {
    title: form.title.trim(),
    description: form.description.trim() || undefined,
    // A past due date is valid on an existing task. Only send it when the user changed it,
    // so edits to other fields are not rejected by the due-date check.
    dueDate: !savedForm || form.dueDate !== savedForm.dueDate ? (form.dueDate || undefined) : undefined,
    dueTime: !savedForm || form.dueTime !== savedForm.dueTime ? (form.dueTime || undefined) : undefined,
    recurrence: form.recurrence,
    priority: form.priority,
    status: form.status,
    contactId: form.contactId || undefined,
    assetId: form.assetId || undefined,
    // Sending the AI assignee again queues a new run when the task is not currently assigned.
    // Only send it when the user actually changes the assignee. Run again is a separate request.
    ...(assigneeChanged
      ? {
          assignedToUserId: form.assignedToAi ? '00000000-0000-0000-0000-000000000000' : (form.assignedToUserId || '00000000-0000-0000-0000-000000000000'),
          assignedToAi: form.assignedToAi,
        }
      : {}),
    labels: form.labels,
    // An empty object clears the place; undefined would leave it untouched.
    visitPlace: form.visitPlace ?? emptyVisitPlace,
  };
};

const toCreateRequest = (form: EditorForm): CreateTaskRequest => ({
  title: form.title.trim(),
  description: form.description.trim() || undefined,
  dueDate: form.dueDate || undefined,
  dueTime: form.dueTime || undefined,
  recurrence: form.recurrence,
  priority: form.priority,
  categoryId: form.categoryId || undefined,
  contactId: form.contactId || undefined,
  assetId: form.assetId || undefined,
  assignedToUserId: form.assignedToAi ? undefined : (form.assignedToUserId || undefined),
  assignedToAi: form.assignedToAi,
  labels: form.labels,
  visitPlace: form.visitPlace,
});

const serializeEditorForm = (form: EditorForm) => JSON.stringify(form);

export const TaskEditor: React.FC<TaskEditorProps> = ({
  taskId,
  onSaved,
  onCancel,
  embedded,
  initialStatusOptions,
  initialCategoryOptions = [],
}) => {
  const { t } = useTranslation(['tasks', 'common']);
  const { id: routeId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  const id = taskId ?? routeId;
  const isEmbedded = embedded ?? Boolean(onSaved || onCancel);

  const isEdit = Boolean(id);
  const canManage = hasPermission([PermissionCodes.tasksManage]);
  const canViewTaskAiActivity = hasPermission(PermissionCodes.tasksAiActivityRead);

  const [form, setForm] = useState<EditorForm>(() => {
    const statuses = resolveSeedStatusOptions(initialStatusOptions);
    return createInitialForm(statuses, initialCategoryOptions);
  });
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const validationBannerRef = useRef<HTMLDivElement | null>(null);
  const validationNotice = useMemo(() => {
    if (!error) {
      return null;
    }

    const items = taskValidationItems(error, (key, options) => t(key, options));
    if (items.length === 0) {
      return null;
    }

    return {
      items,
      isFieldValidation: items.some((item) => Boolean(item.label)),
    };
  }, [error, t]);

  useEffect(() => {
    if (!error) {
      return;
    }

    validationBannerRef.current?.scrollIntoView({ block: 'nearest' });
  }, [error]);
  const [contacts, setContacts] = useState<Array<{ id: string; name: string }>>([]);
  const [assets, setAssets] = useState<Array<{ id: string; name: string }>>([]);
  const [employees, setEmployees] = useState<Array<{ id: string; name: string; colorPreference?: string; profileImageUrl?: string }>>([]);
  const [categories, setCategories] = useState<TaskCategoryItem[]>(initialCategoryOptions);
  const [labelOptions, setLabelOptions] = useState<TaskLabelItem[]>([]);
  const [labelDraft, setLabelDraft] = useState('');
  const [labelSuggestionsOpen, setLabelSuggestionsOpen] = useState(false);
  const [statusOptions, setStatusOptions] = useState<TaskStatusDefinition[]>(() => resolveSeedStatusOptions(initialStatusOptions));
  const [metadataReady, setMetadataReady] = useState(() => resolveSeedStatusOptions(initialStatusOptions).length > 0);
  const [comments, setComments] = useState<TaskCommentItem[]>([]);
  const [aiAgentTasksEnabled, setAiAgentTasksEnabled] = useState(false);
  const [aiReplyPendingSince, setAiReplyPendingSince] = useState<string | null>(null);
  const [attachments, setAttachments] = useState<TaskAttachmentItem[]>([]);
  const [activities, setActivities] = useState<TaskActivityItem[]>([]);
  const [aiOperations, setAiOperations] = useState<TaskAiOperationItem[]>([]);
  const [aiOperationsLoading, setAiOperationsLoading] = useState(false);
  const [aiOperationsError, setAiOperationsError] = useState<string | null>(null);
  const [loadingCollaboration, setLoadingCollaboration] = useState(false);
  const [commentDraft, setCommentDraft] = useState('');
  const [savingComment, setSavingComment] = useState(false);
  const [taskKey, setTaskKey] = useState('');
  const [aiRunStatus, setAiRunStatus] = useState<TaskAiRunStatus>('none');
  const [conversationLinks, setConversationLinks] = useState<TaskConversationLinkItem[]>([]);
  const [relatedTasks, setRelatedTasks] = useState<TaskRelatedTaskLinkItem[]>([]);
  const [relatedTaskSearch, setRelatedTaskSearch] = useState('');
  const [relatedTaskCandidates, setRelatedTaskCandidates] = useState<Array<{ id: string; taskKey: string; title: string }>>([]);
  const [loadingRelatedTaskCandidates, setLoadingRelatedTaskCandidates] = useState(false);
  const [linkingRelatedTaskId, setLinkingRelatedTaskId] = useState<string | null>(null);
  const [unlinkingRelatedTaskIds, setUnlinkingRelatedTaskIds] = useState<string[]>([]);
  const [collabTab, setCollabTab] = useState<'comments' | 'activity' | 'aiActivity'>('comments');
  const [descriptionDragActive, setDescriptionDragActive] = useState(false);
  const [descriptionUploading, setDescriptionUploading] = useState(false);
  const [commentDragActive, setCommentDragActive] = useState(false);
  const [commentUploading, setCommentUploading] = useState(false);
  const [deletingAttachmentId, setDeletingAttachmentId] = useState<string | null>(null);
  const [pendingCommentAttachments, setPendingCommentAttachments] = useState<PendingAttachment[]>([]);
  const [lightboxAttachment, setLightboxAttachment] = useState<TaskAttachmentItem | null>(null);
  const [subtasks, setSubtasks] = useState<SubtaskItem[]>([]);
  const [subtaskDraft, setSubtaskDraft] = useState('');
  const [savingSubtask, setSavingSubtask] = useState(false);
  const [subtaskError, setSubtaskError] = useState<string | null>(null);
  const [highlightedSubtaskId, setHighlightedSubtaskId] = useState<string | null>(null);
  const subtaskInputRef = useRef<HTMLInputElement | null>(null);
  const activeTaskIdRef = useRef<string | undefined>(id);
  activeTaskIdRef.current = id;
  const [parentTaskId, setParentTaskId] = useState<string | null>(null);
  const [parentTaskKey, setParentTaskKey] = useState<string | null>(null);
  const [reportedBy, setReportedBy] = useState<string>('');
  const [createdAt, setCreatedAt] = useState<string>('');
  const [autosaveState, setAutosaveState] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [unlinkingConversationIds, setUnlinkingConversationIds] = useState<string[]>([]);
  const lastSavedFormRef = useRef<string>(serializeEditorForm(defaultForm));
  const lastAutosaveErrorSnapshotRef = useRef<string | null>(null);
  const autosaveStateTimeoutRef = useRef<number | null>(null);
  const relatedTaskSearchTimeoutRef = useRef<number | null>(null);
  const relatedTaskSearchRequestIdRef = useRef(0);
  const assigneeForStatus = form.assignedToAi ? AI_ASSISTANT_ID : form.assignedToUserId;
  const blockedStatusesByAssignee = getBlockedTaskStatusesByAssignee(form.status, statusOptions, assigneeForStatus);
  const statusBlockedByAssignee = blockedStatusesByAssignee.length > 0;

  useEffect(() => {
    if (!lightboxAttachment) return;
    const onKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') setLightboxAttachment(null); };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [lightboxAttachment]);

  useEffect(() => {
    return () => {
      if (autosaveStateTimeoutRef.current !== null) {
        window.clearTimeout(autosaveStateTimeoutRef.current);
      }
      if (relatedTaskSearchTimeoutRef.current !== null) {
        window.clearTimeout(relatedTaskSearchTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const loadMetadata = async () => {
      try {
        const [contactsData, assetsData, employeesData, categoriesData, labelsData, statusesData] = await Promise.all([
          getContacts(),
          getAssets(),
          employeeService.getEmployees({ isActive: true, sortBy: 'name', sortDir: 'asc', pageSize: 200 }),
          taskService.listTaskCategories(),
          taskService.listTaskLabels(),
          taskService.listTaskStatuses(),
        ]);
        setContacts(contactsData.map(contact => ({ id: contact.id, name: contact.name })));
        setAssets(assetsData.map(asset => ({ id: asset.id, name: asset.name })));
        setEmployees(employeesData.map(employee => ({ id: employee.id, name: employee.name || employee.email, colorPreference: employee.colorPreference, profileImageUrl: employee.profileImageUrl })));
        setCategories(categoriesData);
        setLabelOptions(labelsData);
        const sortedStatuses = sortTaskStatuses(statusesData);
        setStatusOptions(sortedStatuses);
        setForm(prev => ({
          ...prev,
          categoryId: prev.categoryId || pickDefaultCategoryId(categoriesData),
          status: prev.status || pickDefaultStatusCode(sortedStatuses),
        }));
      } catch {
        // Keep editor usable even if relation metadata fails.
      } finally {
        setMetadataReady(true);
      }
    };

    void loadMetadata();
  }, []);

  useEffect(() => {
    if (!id) return undefined;

    let cancelled = false;

    // Per-task UI state must not leak when navigating parent <-> subtask within the same editor instance.
    setError(null);
    setSubtaskError(null);
    setSubtaskDraft('');
    setHighlightedSubtaskId(null);
    setCommentDraft('');
    setPendingCommentAttachments(prev => {
      revokePendingAttachments(prev);
      return [];
    });
    setAiReplyPendingSince(null);
    setRelatedTaskSearch('');
    setRelatedTaskCandidates([]);
    setSubtasks([]);
    setConversationLinks([]);
    setRelatedTasks([]);
    setComments([]);
    setAttachments([]);
    setActivities([]);
    if (!isEmbedded && typeof window !== 'undefined') {
      window.scrollTo({ top: 0 });
    }

    const loadTask = async () => {
      setLoading(true);
      try {
        const task = await taskService.getTaskById(id);
        if (cancelled) return;
        if (!task) {
          setError(t('notFound', { ns: 'tasks' }));
          return;
        }

        const nextForm = toEditorForm(task);
        setForm(nextForm);
        lastSavedFormRef.current = serializeEditorForm(nextForm);
        setTaskKey(task.taskKey || '');
        setAiRunStatus(task.aiRunStatus || 'none');
        setConversationLinks(task.conversationLinks || []);
        setRelatedTasks(task.relatedTasks || []);
        setSubtasks(task.subtasks || []);
        setParentTaskId(task.parentTaskId || null);
        setParentTaskKey(task.parentTaskKey || null);
        setReportedBy(task.createdBy || '');
        setCreatedAt(task.createdAt || '');
        lastAutosaveErrorSnapshotRef.current = null;
        setError(null);
      } catch {
        if (!cancelled) setError(t('failedToLoadOne', { ns: 'tasks' }));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void loadTask();

    return () => {
      cancelled = true;
    };
  }, [id, isEmbedded, t]);

  const employeeById = useMemo(() => {
    return employees.reduce<Record<string, typeof employees[number]>>((acc, e) => { acc[e.id] = e; return acc; }, {});
  }, [employees]);

  const isAiActor = (employeeId: string) => isAiAssistantId(employeeId);

  const aiAgentName = t('aiAgent', { ns: 'tasks' });

  // Names that render as mention chips in the composer and in posted comments.
  const mentionTerms = useMemo<MentionTerm[]>(() => {
    const terms: MentionTerm[] = employees
      .filter(employee => employee.name.trim())
      .map(employee => ({ name: employee.name.trim(), kind: 'employee' as const }));
    if (aiAgentTasksEnabled && aiAgentName.trim()) {
      terms.push({ name: aiAgentName.trim(), kind: 'ai' });
    }
    return terms;
  }, [aiAgentName, aiAgentTasksEnabled, employees]);

  const mentionChipClassNames = useMemo(
    () => ({ employee: styles.mentionChip, ai: `${styles.mentionChip} ${styles.mentionChipAi}` }),
    [],
  );

  const renderCommentHtml = (content: string) =>
    decorateMentionsHtml(sanitizeRichTextHtml(content), mentionTerms, mentionChipClassNames);

  const resolveActorName = (employeeId: string) => {
    if (isAiActor(employeeId)) {
      return t('aiAgent', { ns: 'tasks' });
    }

    return employeeById[employeeId]?.name || t('unknownAssignee', { ns: 'tasks' });
  };

  const aiRunStatusLabel = (status: TaskAiRunStatus) => {
    switch (status) {
      case 'queued':
        return t('aiRunQueued', { ns: 'tasks' });
      case 'running':
        return t('aiRunRunning', { ns: 'tasks' });
      case 'succeeded':
        return t('aiRunSucceeded', { ns: 'tasks' });
      case 'failed':
        return t('aiRunFailed', { ns: 'tasks' });
      case 'needsReview':
        return t('aiRunNeedsReview', { ns: 'tasks' });
      default:
        return '';
    }
  };

  const requeueAiRun = async () => {
    if (!id) return;
    setSaving(true);
    try {
      const updated = await taskService.updateTask(id, { requeueAiRun: true, assignedToAi: true });
      if (updated) {
        setAiRunStatus(updated.aiRunStatus || 'queued');
        setForm(current => ({ ...current, assignedToAi: true, assignedToUserId: '' }));
      }
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : t('failedToSave', { ns: 'tasks' }));
    } finally {
      setSaving(false);
    }
  };

  const formatCommentDate = (iso: string) => {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffH = Math.floor(diffMin / 60);
    if (diffH < 24) return `${diffH}h ago`;
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: d.getFullYear() !== now.getFullYear() ? 'numeric' : undefined });
  };

  useEffect(() => {
    const loadCollaboration = async () => {
      if (!id) return;

      setLoadingCollaboration(true);
      try {
        const [loadedComments, loadedAttachments, loadedActivities] = await Promise.all([
          taskService.listTaskComments(id),
          taskService.listTaskAttachments(id),
          taskService.listTaskActivities(id),
        ]);
        setComments(loadedComments);
        setAttachments(loadedAttachments);
        setActivities(loadedActivities);
      } catch {
        // Keep editor usable even if comments/activity fails.
      } finally {
        setLoadingCollaboration(false);
      }
    };

    void loadCollaboration();
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    void taskService.getAiAgentTasksEnabled()
      .then(enabled => {
        if (!cancelled) setAiAgentTasksEnabled(enabled);
      })
      .catch(() => {
        if (!cancelled) setAiAgentTasksEnabled(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!id || !aiReplyPendingSince) return undefined;
    let cancelled = false;
    const startedAt = Date.now();
    const pendingTime = new Date(aiReplyPendingSince).getTime();
    const tick = async () => {
      if (cancelled || Date.now() - startedAt > 90000) {
        if (!cancelled && Date.now() - startedAt > 90000) setAiReplyPendingSince(null);
        return;
      }
      try {
        const loaded = await taskService.listTaskComments(id);
        if (cancelled) return;
        const replied = loaded.some(comment =>
          isAiAssistantId(comment.createdBy) && new Date(comment.createdAt).getTime() > pendingTime);
        setComments(loaded);
        if (replied) setAiReplyPendingSince(null);
      } catch {
        // Keep waiting. The next tick retries.
      }
    };
    const timer = window.setInterval(() => { void tick(); }, 4000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [aiReplyPendingSince, id]);

  useEffect(() => {
    if (!id || !canViewTaskAiActivity) {
      setAiOperations([]);
      setAiOperationsError(null);
      return;
    }

    let cancelled = false;
    const loadAiOperations = async () => {
      setAiOperationsLoading(true);
      setAiOperationsError(null);
      try {
        const items = await taskService.listTaskAiOperations(id);
        if (!cancelled) {
          setAiOperations(items);
        }
      } catch (requestError) {
        if (!cancelled) {
          setAiOperations([]);
          setAiOperationsError(requestError instanceof Error
            ? requestError.message
            : t('failedToLoadAiActivity', { ns: 'tasks', defaultValue: 'Failed to load AI activity.' }));
        }
      } finally {
        if (!cancelled) {
          setAiOperationsLoading(false);
        }
      }
    };

    void loadAiOperations();
    return () => {
      cancelled = true;
    };
  }, [canViewTaskAiActivity, id, t]);

  useEffect(() => {
    return () => {
      revokePendingAttachments(pendingCommentAttachments);
    };
  }, [pendingCommentAttachments]);

  const descriptionAttachments = useMemo(
    () => attachments.filter(attachment => !attachment.taskCommentId),
    [attachments],
  );

  const commentAttachmentsById = useMemo(() => {
    return attachments.reduce<Record<string, TaskAttachmentItem[]>>((acc, attachment) => {
      if (!attachment.taskCommentId) return acc;
      if (!acc[attachment.taskCommentId]) {
        acc[attachment.taskCommentId] = [];
      }
      acc[attachment.taskCommentId].push(attachment);
      return acc;
    }, {});
  }, [attachments]);

  const onChange = <K extends keyof EditorForm>(key: K, value: EditorForm[K]) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  const addLabel = useCallback((rawLabel: string) => {
    const nextLabel = rawLabel.trim();
    if (!nextLabel) return;

    setForm(prev => {
      if (prev.labels.some(label => label.toLowerCase() === nextLabel.toLowerCase())) {
        return prev;
      }

      return {
        ...prev,
        labels: [...prev.labels, nextLabel],
      };
    });

    setLabelOptions(prev => {
      if (prev.some(label => label.name.toLowerCase() === nextLabel.toLowerCase())) {
        return prev;
      }

      return [...prev, { id: `local-${nextLabel.toLowerCase()}`, name: nextLabel }].sort((a, b) => a.name.localeCompare(b.name));
    });

    setLabelDraft('');
    setLabelSuggestionsOpen(false);
  }, []);

  const removeLabel = useCallback((labelToRemove: string) => {
    setForm(prev => ({
      ...prev,
      labels: prev.labels.filter(label => label.toLowerCase() !== labelToRemove.toLowerCase()),
    }));
  }, []);

  const suggestedLabels = useMemo(() => {
    const selectedLabelSet = new Set(form.labels.map(label => label.toLowerCase()));
    const query = labelDraft.trim().toLowerCase();

    return labelOptions
      .filter(label => !selectedLabelSet.has(label.name.toLowerCase()))
      .filter(label => !query || label.name.toLowerCase().includes(query))
      .slice(0, 10);
  }, [form.labels, labelDraft, labelOptions]);

  const categorySelectOptions = useMemo(() => buildCategorySelectOptions(categories), [categories]);

  const categoryById = useMemo(() => {
    return categories.reduce<Record<string, string>>((acc, category) => {
      acc[category.id] = formatCategoryLabel(category, categories);
      return acc;
    }, {});
  }, [categories]);

  const contactById = useMemo(() => {
    return contacts.reduce<Record<string, string>>((acc, contact) => {
      acc[contact.id] = contact.name;
      return acc;
    }, {});
  }, [contacts]);

  const assetById = useMemo(() => {
    return assets.reduce<Record<string, string>>((acc, asset) => {
      acc[asset.id] = asset.name;
      return acc;
    }, {});
  }, [assets]);

  const assignedEmployee = useMemo(
    () => (form.assignedToUserId ? employeeById[form.assignedToUserId] : undefined),
    [employeeById, form.assignedToUserId],
  );

  const isDueOverdue = useMemo(() => {
    if (!form.dueDate || isTaskStatusCompleted(form.status, statusOptions)) {
      return false;
    }

    const dueDate = parseDateInputValue(form.dueDate);
    if (!dueDate) {
      return false;
    }

    return dueDate.getTime() < Date.now();
  }, [form.dueDate, form.status, statusOptions]);

  const resolveActivityFieldLabel = useCallback((field: string) => {
    switch (field) {
      case 'AssignedToUserId':
        return t('assigneeLabel', { ns: 'tasks' });
      case 'CategoryId':
        return t('categoryLabel', { ns: 'tasks' });
      case 'ContactId':
        return t('contact', { ns: 'tasks' });
      case 'AssetId':
        return t('asset', { ns: 'tasks' });
      case 'DueDate':
        return t('dueDate', { ns: 'tasks' });
      case 'DueTime':
        return t('dueTime', { ns: 'tasks' });
      case 'Recurrence':
        return t('recurrenceLabel', { ns: 'tasks' });
      case 'Priority':
        return t('priorityLabel', { ns: 'tasks' });
      case 'Status':
        return t('statusLabel', { ns: 'tasks' });
      case 'Labels':
        return t('labelsLabel', { ns: 'tasks', defaultValue: 'Labels' });
      case 'Subtasks':
        return t('subtasks', { ns: 'tasks', defaultValue: 'Subtasks' });
      case 'RelatedTasks':
        return t('relatedTasksTitle', { ns: 'tasks', defaultValue: 'Related tasks' });
      default:
        return field;
    }
  }, [t]);

  const resolveActivityValue = useCallback((field: string, value?: string) => {
    if (!value) {
      return '-';
    }

    if (field === 'AssignedToUserId') {
      return employeeById[value]?.name || value;
    }

    if (field === 'CategoryId') {
      return categoryById[value] || value;
    }

    if (field === 'ContactId') {
      return contactById[value] || value;
    }

    if (field === 'AssetId') {
      return assetById[value] || value;
    }

    if (field === 'Status') {
      return getTaskStatusLabel(value, statusOptions);
    }

    if (field === 'Priority') {
      return t(`priority.${value.toLowerCase()}`, { ns: 'tasks', defaultValue: value });
    }

    if (field === 'Recurrence') {
      return t(`recurrence.${value}`, { ns: 'tasks', defaultValue: value });
    }

    if (field === 'DueDate') {
      return formatTaskDueDateLabel(value);
    }

    if (field === 'DueTime') {
      return formatTimeInputLabel(value);
    }

    return value;
  }, [assetById, categoryById, contactById, employeeById, statusOptions, t]);

  const resolveActivityMessage = useCallback((activity: TaskActivityItem) => {
    if (activity.type === 'updated' && activity.field === 'Subtasks' && activity.newValue) {
      return t('activitySubtaskAdded', {
        ns: 'tasks',
        key: activity.newValue,
        defaultValue: `Subtask added: ${activity.newValue}`,
      });
    }

    if (activity.type === 'updated' && activity.field) {
      return t('activityUpdatedMessage', {
        ns: 'tasks',
        field: resolveActivityFieldLabel(activity.field),
        defaultValue: `${resolveActivityFieldLabel(activity.field)} updated`,
      });
    }

    return activity.message;
  }, [resolveActivityFieldLabel, t]);

  const markAutosaveSettled = useCallback(() => {
    setAutosaveState('saved');
    if (autosaveStateTimeoutRef.current !== null) {
      window.clearTimeout(autosaveStateTimeoutRef.current);
    }
    autosaveStateTimeoutRef.current = window.setTimeout(() => {
      setAutosaveState('idle');
      autosaveStateTimeoutRef.current = null;
    }, 1500);
  }, []);

  const autosaveTask = useCallback(async () => {
    if (!isEdit || !id) return;

    const formSnapshot = form;
    const serializedSnapshot = serializeEditorForm(formSnapshot);

    if (!formSnapshot.title.trim()) {
      lastAutosaveErrorSnapshotRef.current = serializedSnapshot;
      setError(t('titleRequired', { ns: 'tasks' }));
      return;
    }

    setSaving(true);
    setAutosaveState('saving');
    setError(null);

    try {
      const savedForm = lastSavedFormRef.current
        ? JSON.parse(lastSavedFormRef.current) as EditorForm
        : undefined;
      const updated = await taskService.updateTask(id, toUpdateRequest(formSnapshot, savedForm));
      if (activeTaskIdRef.current !== id) {
        // The user navigated to another task (e.g. a subtask) while this save was in flight.
        setAutosaveState('idle');
        return;
      }
      if (updated) {
        const nextForm = toEditorForm(updated);
        lastSavedFormRef.current = serializedSnapshot;
        setTaskKey(updated.taskKey || '');
        setAiRunStatus(updated.aiRunStatus || 'none');
        setConversationLinks(updated.conversationLinks || []);
        setRelatedTasks(updated.relatedTasks || []);
        setForm(current => {
          if (serializeEditorForm(current) !== serializedSnapshot) {
            return current;
          }

          return {
            ...nextForm,
            title: current.title,
            description: current.description,
            dueDate: current.dueDate,
            dueTime: current.dueTime,
          };
        });
      } else {
        lastSavedFormRef.current = serializedSnapshot;
      }
      lastAutosaveErrorSnapshotRef.current = null;
      markAutosaveSettled();
    } catch (requestError) {
      lastAutosaveErrorSnapshotRef.current = serializedSnapshot;
      setAutosaveState('idle');
      setError(requestError instanceof Error ? requestError.message : t('failedToSave', { ns: 'tasks' }));
    } finally {
      setSaving(false);
    }
  }, [form, id, isEdit, markAutosaveSettled, t]);

  useEffect(() => {
    if (!isEdit || !id || loading || saving) return;
    const serialized = serializeEditorForm(form);
    if (serialized === lastSavedFormRef.current || serialized === lastAutosaveErrorSnapshotRef.current) return;

    const timeoutId = window.setTimeout(() => {
      void autosaveTask();
    }, 700);

    return () => window.clearTimeout(timeoutId);
  }, [autosaveTask, form, id, isEdit, loading, saving]);

  if (!canManage) {
    return <div className="error">{t('noPermission', { ns: 'app' })}</div>;
  }

  if (loading || (isEmbedded && !isEdit && !metadataReady)) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: 40 }}>
        <Spinner text={t('loading', { ns: 'tasks' })} />
      </div>
    );
  }

  const onSave = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!form.title.trim()) {
      setError(t('titleRequired', { ns: 'tasks' }));
      return;
    }

    setSaving(true);
    setError(null);

    try {
      if (isEdit && id) {
        const savedForm = lastSavedFormRef.current
          ? JSON.parse(lastSavedFormRef.current) as EditorForm
          : undefined;
        await taskService.updateTask(id, toUpdateRequest(form, savedForm));
      } else {
        const createRequest = toCreateRequest(form);
        await taskService.createTask(createRequest);
      }

      if (onSaved) {
        onSaved();
      } else {
        navigate('/tasks');
      }
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : t('failedToSave', { ns: 'tasks' }));
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async () => {
    if (!id) return;
    if (!window.confirm(t('deleteConfirm', { ns: 'tasks' }))) return;

    setSaving(true);
    try {
      await taskService.deleteTask(id);
      if (onSaved) {
        onSaved();
      } else {
        navigate('/tasks');
      }
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : t('failedToDelete', { ns: 'tasks' }));
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
      return;
    }
    navigate('/tasks');
  };

  const formatConversationLinkDate = (iso: string) => {
    if (!iso) return '';
    return formatDate(iso, { day: 'numeric', month: 'short' });
  };

  const handleOpenConversation = (conversationId: string) => {
    navigate(`/conversations/${conversationId}`);
  };

  const handleUnlinkConversation = async (conversationId: string) => {
    if (!id) return;

    setUnlinkingConversationIds(prev => [...prev, conversationId]);
    try {
      const updated = await taskService.unlinkTaskFromConversation(id, conversationId);
      if (updated) {
        setConversationLinks(updated.conversationLinks || []);
        setRelatedTasks(updated.relatedTasks || []);
        setRelatedTasks(updated.relatedTasks || []);
        setTaskKey(updated.taskKey || '');
        setSubtasks(updated.subtasks || []);
        setParentTaskId(updated.parentTaskId || null);
        setParentTaskKey(updated.parentTaskKey || null);
      }
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : t('failedToSave', { ns: 'tasks' }));
    } finally {
      setUnlinkingConversationIds(prev => prev.filter(item => item !== conversationId));
    }
  };

  const loadRelatedTaskCandidates = async (searchText: string) => {
    const trimmed = searchText.trim();
    if (!id || trimmed.length < 2) {
      relatedTaskSearchRequestIdRef.current += 1;
      setLoadingRelatedTaskCandidates(false);
      setRelatedTaskCandidates([]);
      return;
    }

    const requestId = relatedTaskSearchRequestIdRef.current + 1;
    relatedTaskSearchRequestIdRef.current = requestId;
    setLoadingRelatedTaskCandidates(true);
    try {
      const result = await taskService.listTasks({ page: 1, pageSize: 10, searchText: trimmed });
      if (requestId !== relatedTaskSearchRequestIdRef.current) return;
      const linkedIds = new Set(relatedTasks.map(link => link.relatedTaskId));
      setRelatedTaskCandidates(
        result.items
          .filter(item => item.id !== id && !linkedIds.has(item.id))
          .map(item => ({ id: item.id, taskKey: item.taskKey, title: item.title })),
      );
    } catch {
      if (requestId !== relatedTaskSearchRequestIdRef.current) return;
      setRelatedTaskCandidates([]);
    } finally {
      if (requestId === relatedTaskSearchRequestIdRef.current) {
        setLoadingRelatedTaskCandidates(false);
      }
    }
  };

  const handleOpenRelatedTask = (relatedTaskId: string) => {
    navigate(`/tasks/${relatedTaskId}`);
  };

  const handleLinkRelatedTask = async (relatedTaskId: string) => {
    if (!id || !relatedTaskId) return;
    setLinkingRelatedTaskId(relatedTaskId);
    try {
      const updated = await taskService.linkRelatedTask(id, relatedTaskId);
      if (updated) {
        setRelatedTasks(updated.relatedTasks || []);
        setConversationLinks(updated.conversationLinks || []);
        setSubtasks(updated.subtasks || []);
        setTaskKey(updated.taskKey || '');
      }
      const refreshedActivities = await taskService.listTaskActivities(id);
      setActivities(refreshedActivities);
      setRelatedTaskSearch('');
      setRelatedTaskCandidates([]);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : t('failedToSave', { ns: 'tasks' }));
    } finally {
      setLinkingRelatedTaskId(null);
    }
  };

  const handleUnlinkRelatedTask = async (relatedTaskId: string) => {
    if (!id) return;
    setUnlinkingRelatedTaskIds(prev => [...prev, relatedTaskId]);
    try {
      const updated = await taskService.unlinkRelatedTask(id, relatedTaskId);
      if (updated) {
        setRelatedTasks(updated.relatedTasks || []);
        setConversationLinks(updated.conversationLinks || []);
        setSubtasks(updated.subtasks || []);
        setTaskKey(updated.taskKey || '');
      }
      const refreshedActivities = await taskService.listTaskActivities(id);
      setActivities(refreshedActivities);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : t('failedToSave', { ns: 'tasks' }));
    } finally {
      setUnlinkingRelatedTaskIds(prev => prev.filter(item => item !== relatedTaskId));
    }
  };

  const loadCommentMentionSuggestions = async (query: string) => {
    const normalized = query.trim().toLowerCase();
    const employeeOptions = employees
      .filter(employee => {
        const name = employee.name.toLowerCase();
        return normalized.length === 0 || name.includes(normalized);
      })
      .slice(0, 8)
      .map(employee => ({
        id: employee.id,
        name: employee.name,
        kind: 'employee' as const,
        avatarUrl: employee.profileImageUrl,
        colorPreference: employee.colorPreference,
      }));
    const agentName = t('aiAgent', { ns: 'tasks' });
    const showAgent = aiAgentTasksEnabled
      && (normalized.length === 0 || agentName.toLowerCase().includes(normalized));
    if (!showAgent) return employeeOptions;
    return [
      {
        id: AI_ASSISTANT_ID,
        name: agentName,
        kind: 'ai' as const,
        subtitle: t('aiAgentMentionHint', { ns: 'tasks' }),
      },
      ...employeeOptions,
    ];
  };

  const extractCommentMentions = (htmlContent: string) => {
    if (!htmlContent) return [];

    const container = document.createElement('div');
    container.innerHTML = htmlContent;
    const plainText = (container.textContent || '').trim();
    if (!plainText) return [];

    const escapeRegex = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const mentions: TaskCommentMentionInput[] = employees
      .filter(employee => {
        const name = employee.name.trim();
        if (!name) return false;
        const pattern = new RegExp(`(^|\\s)@${escapeRegex(name)}(?=\\s|$|[.,!?;:])`, 'i');
        return pattern.test(plainText);
      })
      .map(employee => ({
        mentionedEmployeeId: employee.id,
        mentionText: `@${employee.name}`,
        kind: 'employee' as const,
      }));

    if (aiAgentTasksEnabled) {
      const agentName = t('aiAgent', { ns: 'tasks' }).trim();
      if (agentName) {
        const agentPattern = new RegExp(`(^|\\s)@${escapeRegex(agentName)}(?=\\s|$|[.,!?;:])`, 'i');
        if (agentPattern.test(plainText)) {
          mentions.unshift({
            mentionedEmployeeId: AI_ASSISTANT_ID,
            mentionText: `@${agentName}`,
            kind: 'aiAssistant',
          });
        }
      }
    }

    return mentions;
  };

  const onAddComment = async () => {
    if (!id) return;

    const content = commentDraft.trim();
    const isContentEmpty = !content || content === '<p></p>';
    if (isContentEmpty && pendingCommentAttachments.length === 0) return;

    setSavingComment(true);
    try {
      const mentions = isContentEmpty ? [] : extractCommentMentions(content);
      const mentionedAi = mentions.some(mention => mention.kind === 'aiAssistant');
      const created = await taskService.addTaskComment(
        id,
        isContentEmpty ? t('attachmentAdded', { ns: 'tasks', defaultValue: 'Added attachment' }) : content,
        mentions);
      if (created) {
        if (pendingCommentAttachments.length > 0) {
          setCommentUploading(true);
          for (const pending of pendingCommentAttachments) {
            await taskService.addTaskAttachment(id, pending.file, created.id);
          }
        }

        setComments(prev => [...prev, created]);
        if (mentionedAi && created.createdAt) setAiReplyPendingSince(created.createdAt);
        setCommentDraft('');
        revokePendingAttachments(pendingCommentAttachments);
        setPendingCommentAttachments([]);
      }

      const [refreshedAttachments, refreshedActivities] = await Promise.all([
        taskService.listTaskAttachments(id),
        taskService.listTaskActivities(id),
      ]);
      setAttachments(refreshedAttachments);
      setActivities(refreshedActivities);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : t('failedToSave', { ns: 'tasks' }));
    } finally {
      setCommentUploading(false);
      setSavingComment(false);
    }
  };

  const toSubtaskItem = (task: TaskItem): SubtaskItem => ({
    id: task.id,
    taskKey: task.taskKey,
    title: task.title,
    status: task.status,
    assignedToUserId: task.assignedToUserId,
  });

  const onAddSubtask = async () => {
    if (!id || savingSubtask) return;

    const title = subtaskDraft.trim();
    if (!title) return;

    const parentId = id;
    setSavingSubtask(true);
    setSubtaskError(null);
    try {
      // The endpoint returns the newly created subtask (not the parent), so append it directly.
      const created = await taskService.createSubtask(parentId, title);
      if (activeTaskIdRef.current !== parentId) return;
      if (created) {
        const item = toSubtaskItem(created);
        setSubtasks(prev => (prev.some(existing => existing.id === item.id) ? prev : [...prev, item]));
        setSubtaskDraft('');
        setHighlightedSubtaskId(item.id);
        window.setTimeout(() => {
          setHighlightedSubtaskId(current => (current === item.id ? null : current));
        }, 1800);
        void taskService.listTaskActivities(parentId)
          .then(refreshed => {
            if (activeTaskIdRef.current === parentId) setActivities(refreshed);
          })
          .catch(() => { /* activity refresh is best-effort */ });
      }
    } catch (requestError) {
      if (activeTaskIdRef.current !== parentId) return;
      setSubtaskError(requestError instanceof Error ? requestError.message : t('failedToSave', { ns: 'tasks' }));
    } finally {
      if (activeTaskIdRef.current === parentId) {
        setSavingSubtask(false);
        window.requestAnimationFrame(() => subtaskInputRef.current?.focus());
      }
    }
  };

  const onSubtaskInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      void onAddSubtask();
      return;
    }
    if (event.key === 'Escape' && subtaskDraft) {
      event.preventDefault();
      setSubtaskDraft('');
      setSubtaskError(null);
    }
  };

  const completedSubtaskCount = subtasks.filter(subtask => isTaskStatusCompleted(subtask.status, statusOptions)).length;
  const subtaskProgressPercent = subtasks.length > 0 ? Math.round((completedSubtaskCount / subtasks.length) * 100) : 0;
  const isSubtaskPage = Boolean(parentTaskId);

  const onDescriptionFilesPicked = async (fileList: FileList | null) => {
    if (!id || !fileList || fileList.length === 0) return;

    const { attachments: selected, errors } = selectPendingAttachments(Array.from(fileList));
    if (errors.length > 0) {
      setError(errors.join(' | '));
    }
    if (selected.length === 0) return;

    setDescriptionUploading(true);
    try {
      for (const pending of selected) {
        await taskService.addTaskAttachment(id, pending.file);
      }

      const [refreshedAttachments, refreshedActivities] = await Promise.all([
        taskService.listTaskAttachments(id),
        taskService.listTaskActivities(id),
      ]);
      setAttachments(refreshedAttachments);
      setActivities(refreshedActivities);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : t('failedToSave', { ns: 'tasks' }));
    } finally {
      selected.forEach(revokePendingAttachment);
      setDescriptionUploading(false);
      setDescriptionDragActive(false);
    }
  };

  const onCommentFilesPicked = (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) return;

    const { attachments: selected, errors } = selectPendingAttachments(Array.from(fileList));
    if (errors.length > 0) {
      setError(errors.join(' | '));
    }
    if (selected.length === 0) return;

    setPendingCommentAttachments(prev => [...prev, ...selected]);
  };

  const removePendingComment = (attachmentId: string) => {
    setPendingCommentAttachments(prev => {
      const found = prev.find(attachment => attachment.id === attachmentId);
      if (found) {
        revokePendingAttachment(found);
      }
      return prev.filter(attachment => attachment.id !== attachmentId);
    });
  };

  const onDeleteAttachment = async (attachmentId: string) => {
    if (!id) return;
    setDeletingAttachmentId(attachmentId);
    try {
      await taskService.deleteTaskAttachment(id, attachmentId);
      setAttachments(prev => prev.filter(item => item.id !== attachmentId));
      const refreshedActivities = await taskService.listTaskActivities(id);
      setActivities(refreshedActivities);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : t('failedToDelete', { ns: 'tasks' }));
    } finally {
      setDeletingAttachmentId(null);
    }
  };

  const renderAttachmentPreview = (attachment: TaskAttachmentItem) => {
    const mime = attachment.mimeType.toLowerCase();
    const label = t('viewFullscreen', { ns: 'tasks', defaultValue: 'View fullscreen' });
    const token = getAttachmentTypeToken(attachment.fileName, mime);
    const toneClassMap: Record<AttachmentPreviewTone, string> = {
      pdf: styles.attachmentThumbnailFile_pdf,
      sheet: styles.attachmentThumbnailFile_sheet,
      doc: styles.attachmentThumbnailFile_doc,
      slide: styles.attachmentThumbnailFile_slide,
      archive: styles.attachmentThumbnailFile_archive,
      text: styles.attachmentThumbnailFile_text,
      media: styles.attachmentThumbnailFile_media,
      file: styles.attachmentThumbnailFile_file,
    };
    if (mime.startsWith('image/')) {
      return (
        <button type="button" className={styles.attachmentThumbnailBtn} onClick={() => setLightboxAttachment(attachment)} aria-label={label}>
          <img className={styles.attachmentThumbnailImg} src={attachment.mediaUrl} alt={attachment.fileName} />
        </button>
      );
    }
    if (mime.startsWith('video/')) {
      return (
        <button type="button" className={styles.attachmentThumbnailBtn} onClick={() => setLightboxAttachment(attachment)} aria-label={label}>
          <video className={styles.attachmentThumbnailVideo} preload="metadata" src={attachment.mediaUrl} />
          <span className={styles.attachmentThumbnailPlay}>&#9654;</span>
        </button>
      );
    }
    if (mime.startsWith('audio/')) {
      return (
        <button type="button" className={styles.attachmentThumbnailBtn} onClick={() => setLightboxAttachment(attachment)} aria-label={label}>
          <span className={styles.attachmentThumbnailAudio}>&#9835;</span>
        </button>
      );
    }
    return (
      <span className={`${styles.attachmentThumbnailFile} ${toneClassMap[token.tone]}`} aria-hidden="true">
        <span className={styles.attachmentFileType}>{token.label}</span>
      </span>
    );
  };

  return (
    <div className={`${styles.page} ${isEmbedded ? styles.pageEmbedded : ''}`}>
      {!isEmbedded ? (
        <div className={styles.headerRow}>
          <Breadcrumb items={[
            { label: t('tasks', { ns: 'tasks', defaultValue: 'Tasks' }), path: '/tasks' },
            ...(parentTaskKey && parentTaskId ? [{ label: parentTaskKey, path: `/tasks/${parentTaskId}` }] : []),
            { label: isEdit ? (taskKey || t('editTask', { ns: 'tasks' })) : t('addTask', { ns: 'tasks' }), path: '#' },
          ]} />

          <div className={styles.headerActions}>
            {isEdit ? (
              <span className={styles.autosaveStatus} aria-live="polite">
                {autosaveState === 'saving'
                  ? t('saving', { ns: 'common' })
                  : autosaveState === 'saved'
                    ? t('saved', { ns: 'common', defaultValue: 'Saved' })
                    : t('allChangesSaved', { ns: 'tasks', defaultValue: 'All changes saved' })}
              </span>
            ) : (
              <button className={`btn btn-primary ${actionIconStyles.buttonWithIcon}`} type="submit" form="task-editor-form" disabled={saving}>
                <ButtonWithIcon icon={<SaveIcon />}>
                  {saving ? t('saving', { ns: 'common' }) : t('save', { ns: 'tasks' })}
                </ButtonWithIcon>
              </button>
            )}
          </div>
        </div>
      ) : null}

      <form id="task-editor-form" className={`${styles.form} ${isEmbedded ? styles.formEmbedded : ''}`} onSubmit={isEdit ? event => event.preventDefault() : onSave}>
        <div className={isEmbedded ? styles.embeddedFormBody : undefined}>
        {validationNotice ? (
          <div className={styles.validationBanner} role="alert" ref={validationBannerRef}>
            {validationNotice.isFieldValidation ? (
              <>
                <p className={styles.validationTitle}>{t('validationTitle', { ns: 'tasks' })}</p>
                <ul className={styles.validationList}>
                  {validationNotice.items.map((item, index) => (
                    <li key={`${item.label ?? 'message'}-${index}`}>
                      {item.label ? <strong>{item.label}</strong> : null}
                      {item.label ? ' — ' : null}
                      {item.message}
                    </li>
                  ))}
                </ul>
              </>
            ) : (
              <p className={styles.validationMessage}>
                {validationNotice.items.map((item) => item.message).join(' ')}
              </p>
            )}
          </div>
        ) : null}
        <div className={styles.editorLayout}>
          <div className={styles.leftColumn}>
            <div className={styles.summaryCard}>
              <div className={styles.summaryHeader}>
                {isEdit && parentTaskId && parentTaskKey ? (
                  <Link
                    to={`/tasks/${parentTaskId}`}
                    className={styles.parentTaskChip}
                    title={t('openParentTask', { ns: 'tasks', defaultValue: 'Open parent task {{key}}', key: parentTaskKey })}
                  >
                    <span className={styles.parentTaskChipLabel}>{t('parentTask', { ns: 'tasks', defaultValue: 'Parent' })}</span>
                    <span className={styles.parentTaskChipKey}>{parentTaskKey}</span>
                  </Link>
                ) : null}
                {isEdit && parentTaskId && parentTaskKey && taskKey ? <span className={styles.summaryKeySeparator} aria-hidden="true">/</span> : null}
                {isEdit && taskKey ? <span className={styles.taskKeyBadge}>{taskKey}</span> : null}
                {isEdit && isSubtaskPage ? <span className={styles.subtaskBadge}>{t('subtask', { ns: 'tasks', defaultValue: 'Subtask' })}</span> : null}
                {!isEdit ? <span className={styles.summaryEyebrow}>{t('newTask', { ns: 'tasks', defaultValue: 'New task' })}</span> : null}
              </div>

              <input
                className={`text-input ${styles.summaryTitleInput}`}
                value={form.title}
                onChange={event => onChange('title', event.target.value)}
                maxLength={200}
                placeholder={t('titlePlaceholder', { ns: 'tasks', defaultValue: 'Task title' })}
                aria-label={t('task', { ns: 'tasks' })}
              />

              <div className={styles.descriptionSlot}>
                <RichTextEditor
                  value={form.description}
                  onChange={html => onChange('description', html)}
                  placeholder={t('descriptionPlaceholder', { ns: 'tasks', defaultValue: 'Add a description…' })}
                  minHeight={260}
                  ariaLabel={t('description', { ns: 'tasks' })}
                />
              </div>

              {isEdit ? (
                <div
                  className={`${styles.attachmentDropzone} ${descriptionDragActive ? styles.attachmentDropzoneActive : ''}`}
                  onDragOver={event => {
                    event.preventDefault();
                    setDescriptionDragActive(true);
                  }}
                  onDragLeave={() => setDescriptionDragActive(false)}
                  onDrop={event => {
                    event.preventDefault();
                    void onDescriptionFilesPicked(event.dataTransfer.files);
                  }}
                >
                  <div className={styles.attachmentDropHeader}>
                    <span>{t('descriptionAttachments', { ns: 'tasks', defaultValue: 'Description attachments' })}</span>
                    <label className="btn btn-secondary" htmlFor="task-description-attachments">
                      {t('selectFiles', { ns: 'common', defaultValue: 'Select files' })}
                    </label>
                    <input
                      id="task-description-attachments"
                      type="file"
                      multiple
                      accept={ATTACHMENT_ACCEPT}
                      className={styles.hiddenFileInput}
                      onChange={event => {
                        void onDescriptionFilesPicked(event.target.files);
                        event.currentTarget.value = '';
                      }}
                    />
                  </div>
                  <div className={styles.attachmentDropHint}>
                    {descriptionUploading
                      ? t('uploading', { ns: 'common' })
                      : t('dropFilesHint', { ns: 'tasks', defaultValue: 'Drag and drop files here or use Select files.' })}
                  </div>

                  {descriptionAttachments.length > 0 ? (
                    <div className={styles.attachmentList}>
                      {descriptionAttachments.map(attachment => (
                        <div key={attachment.id} className={styles.attachmentCard}>
                          {renderAttachmentPreview(attachment)}
                          <a className={styles.attachmentItem} href={attachment.mediaUrl} target="_blank" rel="noreferrer">
                            <span className={styles.attachmentName}>{attachment.fileName}</span>
                            <span className={styles.attachmentMeta}>{formatBytes(attachment.fileSize)}</span>
                          </a>
                          <button
                            className={styles.attachmentRemove}
                            type="button"
                            onClick={() => void onDeleteAttachment(attachment.id)}
                            disabled={deletingAttachmentId === attachment.id}
                          >
                            {t('remove', { ns: 'common', defaultValue: 'Remove' })}
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>

            {isEdit && (!isSubtaskPage || subtasks.length > 0) ? (
              <div className={styles.subtasksCard}>
                <div className={styles.subtasksHeader}>
                  <span className={styles.subtasksTitle}>{t('subtasks', { ns: 'tasks', defaultValue: 'Subtasks' })}</span>
                  {subtasks.length > 0 ? (
                    <span
                      className={`${styles.subtasksProgress} ${completedSubtaskCount === subtasks.length ? styles.subtasksProgressDone : ''}`}
                      title={t('subtasksProgressTitle', { ns: 'tasks', defaultValue: '{{done}} of {{total}} completed', done: completedSubtaskCount, total: subtasks.length })}
                    >
                      {completedSubtaskCount}/{subtasks.length}
                    </span>
                  ) : null}
                </div>

                {subtasks.length > 0 ? (
                  <div
                    className={styles.subtasksProgressBar}
                    role="progressbar"
                    aria-valuemin={0}
                    aria-valuemax={subtasks.length}
                    aria-valuenow={completedSubtaskCount}
                    aria-label={t('subtasks', { ns: 'tasks', defaultValue: 'Subtasks' })}
                  >
                    <span className={styles.subtasksProgressFill} style={{ width: `${subtaskProgressPercent}%` }} />
                  </div>
                ) : null}

                {subtasks.length > 0 ? (
                  <ul className={styles.subtasksList}>
                    {subtasks.map(subtask => {
                      const statusDefinition = getTaskStatusDefinition(subtask.status, statusOptions);
                      const statusLabel = getTaskStatusLabel(subtask.status, statusOptions);
                      const completed = isTaskStatusCompleted(subtask.status, statusOptions);
                      const assignee = subtask.assignedToUserId ? employeeById[subtask.assignedToUserId] : undefined;
                      const rowClassName = [
                        styles.subtaskRow,
                        completed ? styles.subtaskRowDone : '',
                        highlightedSubtaskId === subtask.id ? styles.subtaskRowNew : '',
                      ].filter(Boolean).join(' ');

                      return (
                        <li key={subtask.id} className={rowClassName}>
                          <Link
                            to={`/tasks/${subtask.id}`}
                            className={styles.subtaskLink}
                            title={t('openSubtask', { ns: 'tasks', defaultValue: 'Open {{key}}', key: subtask.taskKey })}
                          >
                            <span
                              className={styles.subtaskStatus}
                              style={{ color: statusDefinition?.color, borderColor: statusDefinition?.color }}
                              aria-hidden="true"
                            />
                            <span className={styles.subtaskKey}>{subtask.taskKey}</span>
                            <span className={styles.subtaskTitle}>{subtask.title}</span>
                            <span className={styles.subtaskStatusLabel} style={getStatusBadgeStyle(statusDefinition?.color)}>
                              {statusLabel}
                            </span>
                            {assignee ? (
                              <span className={styles.subtaskAssignee} title={assignee.name}>
                                <EmployeeAvatar
                                  name={assignee.name}
                                  size={22}
                                  imageUrl={assignee.profileImageUrl}
                                  colorPreference={assignee.colorPreference}
                                />
                              </span>
                            ) : null}
                            <span className={styles.subtaskChevron} aria-hidden="true">›</span>
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <div className={styles.subtasksEmpty}>
                    {t('noSubtasks', { ns: 'tasks', defaultValue: 'No subtasks yet. Break this task into smaller steps.' })}
                  </div>
                )}

                {!isSubtaskPage ? (
                  <div className={styles.subtaskComposer}>
                    <div className={`${styles.subtaskInput} ${subtaskError ? styles.subtaskInputError : ''}`}>
                      <input
                        ref={subtaskInputRef}
                        type="text"
                        className="text-input"
                        value={subtaskDraft}
                        maxLength={200}
                        onChange={event => {
                          setSubtaskDraft(event.target.value);
                          if (subtaskError) setSubtaskError(null);
                        }}
                        onKeyDown={onSubtaskInputKeyDown}
                        placeholder={t('addSubtaskPlaceholder', { ns: 'tasks', defaultValue: 'Add a subtask…' })}
                        aria-label={t('addSubtaskPlaceholder', { ns: 'tasks', defaultValue: 'Add a subtask…' })}
                        aria-invalid={subtaskError ? true : undefined}
                        disabled={savingSubtask}
                      />
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => void onAddSubtask()}
                        disabled={!subtaskDraft.trim() || savingSubtask}
                      >
                        {savingSubtask ? t('saving', { ns: 'common' }) : t('add', { ns: 'common', defaultValue: 'Add' })}
                      </button>
                    </div>
                    {subtaskError ? (
                      <div className={styles.subtaskErrorText} role="alert">{subtaskError}</div>
                    ) : (
                      <div className={styles.subtaskComposerHint}>
                        {t('subtaskComposerHint', { ns: 'tasks', defaultValue: 'Press Enter to add. Subtasks inherit this task’s category, contact and asset.' })}
                      </div>
                    )}
                  </div>
                ) : null}
              </div>
            ) : null}

            {isEdit ? (
              <div className={styles.collabCard}>
                <div className={styles.collabTabs}>
                  <button
                    type="button"
                    className={`${styles.collabTabBtn} ${collabTab === 'comments' ? styles.collabTabActive : ''}`}
                    onClick={() => setCollabTab('comments')}
                  >
                    <CommentsIcon className={styles.collabTabIcon} />
                    {t('comments', { ns: 'tasks' })}
                    {comments.length > 0 ? <span className={styles.collabTabCount}>{comments.length}</span> : null}
                  </button>
                  <button
                    type="button"
                    className={`${styles.collabTabBtn} ${collabTab === 'activity' ? styles.collabTabActive : ''}`}
                    onClick={() => setCollabTab('activity')}
                  >
                    <ActivityIcon className={styles.collabTabIcon} />
                    {t('activity', { ns: 'tasks' })}
                    {activities.length > 0 ? <span className={styles.collabTabCount}>{activities.length}</span> : null}
                  </button>
                  {canViewTaskAiActivity ? (
                    <button
                      type="button"
                      className={`${styles.collabTabBtn} ${collabTab === 'aiActivity' ? styles.collabTabActive : ''}`}
                      onClick={() => setCollabTab('aiActivity')}
                    >
                      <ActivityIcon className={styles.collabTabIcon} />
                      {t('aiActivity', { ns: 'tasks', defaultValue: 'AI activity' })}
                      {aiOperations.length > 0 ? <span className={styles.collabTabCount}>{aiOperations.length}</span> : null}
                    </button>
                  ) : null}
                </div>

                {collabTab === 'comments' ? (
                  <>
                    <div className={styles.commentComposer}>
                      <RichTextEditor
                        value={commentDraft}
                        onChange={html => setCommentDraft(html)}
                        placeholder={t('addCommentPlaceholder', { ns: 'tasks' })}
                        minHeight={80}
                        mentionSuggestionsProvider={loadCommentMentionSuggestions}
                        mentionSuggestionsLabel={t('mentionSomeone', { ns: 'tasks', defaultValue: 'Mention someone' })}
                        mentionSuggestionsHint={t('mentionMenuHint', { ns: 'tasks', defaultValue: '↑↓ to move · Enter to select' })}
                        mentionHighlightTerms={mentionTerms}
                      />
                      <div className={styles.commentComposerHint}>
                        {t('mentionComposerHint', { ns: 'tasks', defaultValue: 'Type @ to mention a teammate or the AI Agent.' })}
                      </div>

                      <div
                        className={`${styles.attachmentDropzone} ${commentDragActive ? styles.attachmentDropzoneActive : ''}`}
                        onDragOver={event => {
                          event.preventDefault();
                          setCommentDragActive(true);
                        }}
                        onDragLeave={() => setCommentDragActive(false)}
                        onDrop={event => {
                          event.preventDefault();
                          setCommentDragActive(false);
                          onCommentFilesPicked(event.dataTransfer.files);
                        }}
                      >
                        <div className={styles.attachmentDropHeader}>
                          <span>{t('commentAttachments', { ns: 'tasks', defaultValue: 'Comment attachments' })}</span>
                          <label className="btn btn-secondary" htmlFor="task-comment-attachments">
                            {t('selectFiles', { ns: 'common', defaultValue: 'Select files' })}
                          </label>
                          <input
                            id="task-comment-attachments"
                            type="file"
                            multiple
                            accept={ATTACHMENT_ACCEPT}
                            className={styles.hiddenFileInput}
                            onChange={event => {
                              onCommentFilesPicked(event.target.files);
                              event.currentTarget.value = '';
                            }}
                          />
                        </div>
                        <div className={styles.attachmentDropHint}>
                          {commentUploading
                            ? t('uploading', { ns: 'common' })
                            : t('dropFilesHint', { ns: 'tasks', defaultValue: 'Drag and drop files here or use Select files.' })}
                        </div>

                        {pendingCommentAttachments.length > 0 ? (
                          <div className={styles.attachmentList}>
                            {pendingCommentAttachments.map(attachment => (
                              <div key={attachment.id} className={styles.attachmentItemPending}>
                                <span className={styles.attachmentName}>{attachment.file.name}</span>
                                <span className={styles.attachmentMeta}>{formatBytes(attachment.file.size)}</span>
                                <button className={styles.attachmentRemove} type="button" onClick={() => removePendingComment(attachment.id)}>
                                  {t('remove', { ns: 'common', defaultValue: 'Remove' })}
                                </button>
                              </div>
                            ))}
                          </div>
                        ) : null}
                      </div>

                      <button
                        className={`btn btn-primary ${actionIconStyles.buttonWithIcon}`}
                        type="button"
                        onClick={onAddComment}
                        disabled={savingComment || commentUploading || (!commentDraft.trim() && pendingCommentAttachments.length === 0)}
                      >
                        <ButtonWithIcon icon={<CreateIcon />}>{t('addComment', { ns: 'tasks' })}</ButtonWithIcon>
                      </button>
                    </div>

                    {aiReplyPendingSince ? (
                      <div className={styles.aiReplyPending} role="status">
                        <AiAgentAvatar name={aiAgentName} size={22} pulse />
                        <span>{t('aiAgentReplying', { ns: 'tasks' })}</span>
                        <span className={styles.aiReplyPendingDots} aria-hidden="true">
                          <span /><span /><span />
                        </span>
                      </div>
                    ) : null}
                    <div className={styles.commentList}>
                      {loadingCollaboration ? <div className={styles.timelineEmpty}>{t('loading', { ns: 'tasks' })}</div> : null}
                      {!loadingCollaboration && comments.length === 0 ? <div className={styles.timelineEmpty}>{t('noComments', { ns: 'tasks' })}</div> : null}
                      {comments.map(comment => {
                        const author = employeeById[comment.createdBy];
                        const isAiComment = isAiActor(comment.createdBy);
                        const authorName = isAiComment
                          ? aiAgentName
                          : (author?.name || t('unknownAssignee', { ns: 'tasks' }));
                        return (
                          <div key={comment.id} className={`${styles.commentCard} ${isAiComment ? styles.commentCardAi : ''}`}>
                            <div className={styles.commentHeader}>
                              {isAiComment ? (
                                <AiAgentAvatar name={authorName} size={30} />
                              ) : (
                                <EmployeeAvatar
                                  name={authorName}
                                  imageUrl={author?.profileImageUrl}
                                  colorPreference={author?.colorPreference}
                                  size={30}
                                />
                              )}
                              <span className={styles.commentAuthor}>
                                {authorName}
                                {isAiComment ? <span className={styles.commentAuthorAiBadge}>AI</span> : null}
                              </span>
                              <span className={styles.commentDate} title={formatDateTime(comment.createdAt)}>
                                {formatDateTime(comment.createdAt)}
                              </span>
                            </div>
                            <div className={`${styles.commentBubble} ${isAiComment ? styles.commentBubbleAi : ''}`}>
                              <div className={styles.commentBody} dangerouslySetInnerHTML={{ __html: renderCommentHtml(comment.content) }} />
                              {(commentAttachmentsById[comment.id] || []).length > 0 ? (
                                <div className={styles.commentAttachmentList}>
                                  {(commentAttachmentsById[comment.id] || []).map(attachment => (
                                    <div key={attachment.id} className={styles.attachmentCard}>
                                      {renderAttachmentPreview(attachment)}
                                      <a className={styles.attachmentItem} href={attachment.mediaUrl} target="_blank" rel="noreferrer">
                                        <span className={styles.attachmentName}>{attachment.fileName}</span>
                                        <span className={styles.attachmentMeta}>{formatBytes(attachment.fileSize)}</span>
                                      </a>
                                      <button
                                        className={styles.attachmentRemove}
                                        type="button"
                                        onClick={() => void onDeleteAttachment(attachment.id)}
                                        disabled={deletingAttachmentId === attachment.id}
                                      >
                                        {t('remove', { ns: 'common', defaultValue: 'Remove' })}
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              ) : null}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                ) : collabTab === 'activity' ? (
                  <div className={styles.timelineList}>
                    {loadingCollaboration ? <div className={styles.timelineEmpty}>{t('loading', { ns: 'tasks' })}</div> : null}
                    {!loadingCollaboration && activities.length === 0 ? <div className={styles.timelineEmpty}>{t('noActivity', { ns: 'tasks' })}</div> : null}
                    {activities.map(activity => (
                      <div key={activity.id} className={styles.timelineItem}>
                        <div className={styles.timelineMeta}>
                          <span className={styles.timelineActor}>{resolveActorName(activity.createdBy)}</span>
                          <span title={new Date(activity.createdAt).toLocaleString()}>{formatCommentDate(activity.createdAt)}</span>
                        </div>
                        <div className={styles.timelineBody}>{resolveActivityMessage(activity)}</div>
                        {activity.field ? (
                          <div className={styles.timelineChange}>
                            <span>{resolveActivityFieldLabel(activity.field)}: </span>
                            <span>{resolveActivityValue(activity.field, activity.oldValue)}</span>
                            <span> → </span>
                            <span>{resolveActivityValue(activity.field, activity.newValue)}</span>
                          </div>
                        ) : null}
                      </div>
                    ))}
                  </div>
                ) : (
                  <TaskAiActivityList
                    items={aiOperations}
                    loading={aiOperationsLoading}
                    error={aiOperationsError}
                    formatDate={formatCommentDate}
                  />
                )}
              </div>
            ) : null}
          </div>

          <div className={styles.rightColumn}>
            <div className={styles.sideHero}>
              <div className={styles.sideHeroPills}>
                {form.status ? (
                  <span
                    className={styles.statusPill}
                    style={getStatusBadgeStyle(getTaskStatusDefinition(form.status, statusOptions)?.color)}
                  >
                    {getTaskStatusLabel(form.status, statusOptions)}
                  </span>
                ) : null}
                <span className={`${styles.pill} ${styles[`priority_${form.priority}`]}`}>
                  {t(`priority.${form.priority}`, { ns: 'tasks' })}
                </span>
                {form.dueDate ? (
                  <span className={`${styles.duePill} ${isDueOverdue ? styles.duePillOverdue : ''}`}>
                    {formatTaskDueDateLabel(form.dueDate)}
                    {form.dueTime ? ` · ${formatTimeInputLabel(form.dueTime)}` : ''}
                  </span>
                ) : null}
              </div>

              {isEdit ? (
                <details className={styles.optionsMenu}>
                  <summary
                    className={styles.optionsIconButton}
                    title={t('options', { ns: 'common', defaultValue: 'Options' })}
                    aria-label={t('options', { ns: 'common', defaultValue: 'Options' })}
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
                      <circle cx="3" cy="8" r="1.4" />
                      <circle cx="8" cy="8" r="1.4" />
                      <circle cx="13" cy="8" r="1.4" />
                    </svg>
                  </summary>
                  <div className={styles.optionsPanel}>
                    <button className={styles.optionsDelete} type="button" onClick={onDelete} disabled={saving}>
                      {t('delete', { ns: 'common' })}
                    </button>
                  </div>
                </details>
              ) : null}
            </div>

            <div className={styles.sideCard}>
              <div className={styles.sideCardTitle}>{t('workflowLabel', { ns: 'tasks', defaultValue: 'Workflow' })}</div>

              <div className={styles.metaRows}>
                <label className={`${styles.metaRow} ${styles.metaRowStacked}`}>
                  <span className={styles.metaLabel}>{t('statusLabel', { ns: 'tasks' })}</span>
                  <div className={styles.metaFieldGroup}>
                    <select className={`text-input ${styles.metaControl}`} value={form.status} onChange={event => onChange('status', event.target.value as TaskStatus)}>
                      {getAllowedTaskStatuses(form.status, statusOptions, assigneeForStatus).map(status => (
                        <option key={status} value={status}>{getTaskStatusLabel(status, statusOptions)}</option>
                      ))}
                    </select>
                    {statusBlockedByAssignee ? (
                      <span className={styles.metaHint}>
                        {t('assignBeforeRestrictedStatusHint', {
                          ns: 'tasks',
                          status: getTaskStatusLabel(blockedStatusesByAssignee[0], statusOptions),
                        })}
                      </span>
                    ) : null}
                  </div>
                </label>

                <label className={`${styles.metaRow} ${styles.metaRowStacked}`}>
                  <span className={styles.metaLabel}>{t('priorityLabel', { ns: 'tasks' })}</span>
                  <select className={`text-input ${styles.metaControl}`} value={form.priority} onChange={event => onChange('priority', event.target.value as TaskPriority)}>
                    <option value="low">{t('priority.low', { ns: 'tasks' })}</option>
                    <option value="medium">{t('priority.medium', { ns: 'tasks' })}</option>
                    <option value="high">{t('priority.high', { ns: 'tasks' })}</option>
                    <option value="urgent">{t('priority.urgent', { ns: 'tasks' })}</option>
                  </select>
                </label>
              </div>
            </div>

            <div className={styles.sideCard}>
              <div className={styles.sideCardTitle}>{t('scheduleLabel', { ns: 'tasks', defaultValue: 'Schedule' })}</div>

              <div className={styles.metaRows}>
                <div className={`${styles.metaRow} ${styles.metaRowStacked}`}>
                  <span className={styles.metaLabel}>{t('dueDate', { ns: 'tasks' })}</span>
                  <div className={`${styles.dueDateTimeRow} ${isDueOverdue ? styles.dueDateTimeRowOverdue : ''}`}>
                    <LocalizedDateInput
                      className={`text-input ${styles.metaControl}`}
                      displayClassName={isDueOverdue ? styles.dueOverdueInput : undefined}
                      value={form.dueDate}
                      onValueChange={value => onChange('dueDate', value)}
                      fillWidth
                    />
                    <TimeInput24
                      className={`text-input ${styles.metaControl} ${styles.dueTimeControl}`}
                      value={form.dueTime}
                      onChange={value => onChange('dueTime', value)}
                      aria-label={t('dueTime', { ns: 'tasks' })}
                    />
                  </div>
                </div>

                <label className={`${styles.metaRow} ${styles.metaRowStacked}`}>
                  <span className={styles.metaLabel}>{t('recurrenceLabel', { ns: 'tasks' })}</span>
                  <select className={`text-input ${styles.metaControl}`} value={form.recurrence} onChange={event => onChange('recurrence', event.target.value as TaskRecurrence)}>
                    <option value="doesNotRepeat">{t('recurrence.doesNotRepeat', { ns: 'tasks' })}</option>
                    <option value="daily">{t('recurrence.daily', { ns: 'tasks' })}</option>
                    <option value="weekly">{t('recurrence.weekly', { ns: 'tasks' })}</option>
                    <option value="monthly">{t('recurrence.monthly', { ns: 'tasks' })}</option>
                    <option value="yearly">{t('recurrence.yearly', { ns: 'tasks' })}</option>
                  </select>
                </label>

                <label className={`${styles.metaRow} ${styles.metaRowStacked}`}>
                  <span className={styles.metaLabel}>{t('categoryLabel', { ns: 'tasks' })}</span>
                  {isEdit ? (
                    <span className={styles.metaControl}>
                      {form.categoryId
                        ? (categoryById[form.categoryId] || t('none', { ns: 'tasks' }))
                        : t('none', { ns: 'tasks' })}
                    </span>
                  ) : (
                    <select className={`text-input ${styles.metaControl}`} value={form.categoryId} onChange={event => onChange('categoryId', event.target.value)}>
                      <option value="">{t('none', { ns: 'tasks' })}</option>
                      {categorySelectOptions.map(category => <option key={category.id} value={category.id}>{category.label}</option>)}
                    </select>
                  )}
                </label>
              </div>
            </div>

            <div className={styles.sideCard}>
              <div className={styles.sideCardTitle}>{t('peopleLabel', { ns: 'tasks', defaultValue: 'People' })}</div>

              <div className={styles.metaRows}>
                <label className={`${styles.metaRow} ${styles.metaRowStacked}`}>
                  <span className={styles.metaLabel}>{t('assigneeLabel', { ns: 'tasks' })}</span>
                  <div className={styles.assigneeRow}>
                    <EmployeeAvatar
                      name={form.assignedToAi ? t('aiAgent', { ns: 'tasks' }) : (assignedEmployee?.name || t('unassigned', { ns: 'tasks' }))}
                      imageUrl={assignedEmployee?.profileImageUrl}
                      colorPreference={assignedEmployee?.colorPreference}
                      size={30}
                    />
                    <select
                      className={`text-input ${styles.metaControl} ${styles.assigneeSelect}`}
                      value={form.assignedToAi ? AI_TASK_ASSIGNEE : form.assignedToUserId}
                      onChange={event => {
                        const value = event.target.value;
                        setForm(current => ({
                          ...current,
                          assignedToAi: value === AI_TASK_ASSIGNEE,
                          assignedToUserId: value === AI_TASK_ASSIGNEE ? '' : value,
                        }));
                      }}
                    >
                      <option value="">{t('unassigned', { ns: 'tasks' })}</option>
                      <option value={AI_TASK_ASSIGNEE}>{t('aiAgent', { ns: 'tasks' })}</option>
                      {employees.map(employee => <option key={employee.id} value={employee.id}>{employee.name}</option>)}
                    </select>
                  </div>
                  {form.assignedToAi ? (
                    <div className={styles.aiRunRow}>
                      {aiRunStatus !== 'none' ? <span className={styles.aiRunBadge}>{aiRunStatusLabel(aiRunStatus)}</span> : null}
                      {aiRunStatus === 'failed' || aiRunStatus === 'needsReview' ? (
                        <button type="button" className="btn btn-secondary" disabled={saving} onClick={() => void requeueAiRun()}>
                          {t('runAgain', { ns: 'tasks' })}
                        </button>
                      ) : null}
                    </div>
                  ) : null}
                </label>
                {form.assignedToAi ? (
                  <div className={styles.metaRow}>
                    <TaskAgentUseCaseLink />
                  </div>
                ) : null}

                {isEdit && reportedBy && (
                  <div className={`${styles.metaRow} ${styles.metaRowStacked}`}>
                    <span className={styles.metaLabel}>{t('reportedByLabel', { ns: 'tasks', defaultValue: 'Reported By' })}</span>
                    {isAiActor(reportedBy) ? (
                      <span className={styles.aiReporterValue}>
                        <EmployeeAvatar
                          name={t('aiAgent', { ns: 'tasks' })}
                          colorPreference="#7C3AED"
                          size={22}
                        />
                        {t('aiAgent', { ns: 'tasks' })}
                      </span>
                    ) : (
                      <span className={styles.metaControl}>
                        {employeeById[reportedBy]?.name || t('unknownAssignee', { ns: 'tasks' })}
                      </span>
                    )}
                  </div>
                )}

                {isEdit && createdAt && (
                  <div className={`${styles.metaRow} ${styles.metaRowStacked}`}>
                    <span className={styles.metaLabel}>{t('createdAt', { ns: 'tasks' })}</span>
                    <span className={styles.metaControl}>{formatDateTime(createdAt)}</span>
                  </div>
                )}

                <label className={`${styles.metaRow} ${styles.metaRowStacked}`}>
                  <span className={styles.metaLabel}>{t('contact', { ns: 'tasks' })}</span>
                  <select className={`text-input ${styles.metaControl}`} value={form.contactId} onChange={event => onChange('contactId', event.target.value)}>
                    <option value="">{t('none', { ns: 'tasks' })}</option>
                    {contacts.map(contact => <option key={contact.id} value={contact.id}>{contact.name}</option>)}
                  </select>
                </label>

                <label className={`${styles.metaRow} ${styles.metaRowStacked}`}>
                  <span className={styles.metaLabel}>{t('asset', { ns: 'tasks' })}</span>
                  <select className={`text-input ${styles.metaControl}`} value={form.assetId} onChange={event => onChange('assetId', event.target.value)}>
                    <option value="">{t('none', { ns: 'tasks' })}</option>
                    {assets.map(asset => <option key={asset.id} value={asset.id}>{asset.name}</option>)}
                  </select>
                </label>

                <div className={`${styles.metaRow} ${styles.metaRowStacked}`}>
                  <VisitPlaceFields
                    targetKind="task"
                    value={form.visitPlace}
                    onChange={next => onChange('visitPlace', next)}
                  />
                </div>
              </div>
            </div>

            <div className={styles.sideCard}>
              <div className={styles.sideCardTitle}>{t('labelsLabel', { ns: 'tasks', defaultValue: 'Labels' })}</div>

              <div className={styles.sideCardBody}>
                <div className={styles.labelEditor}>
                    <div className={styles.labelChipList}>
                      {form.labels.map(label => (
                        <span key={label} className={styles.labelChip}>
                          <button
                            type="button"
                            className={styles.labelChipName}
                            title={t('filterByLabel', { ns: 'tasks', label, defaultValue: `Show tasks labeled ${label}` })}
                            onClick={() => {
                              const params = new URLSearchParams();
                              params.set('label', label);
                              navigate(`/tasks?${params.toString()}`);
                            }}
                          >
                            {label}
                          </button>
                          <button
                            type="button"
                            className={styles.labelChipRemove}
                            aria-label={t('removeLabelTitle', { ns: 'tasks', label, defaultValue: `Remove ${label}` })}
                            title={t('removeLabelTitle', { ns: 'tasks', label, defaultValue: `Remove ${label}` })}
                            onClick={() => removeLabel(label)}
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>

                    <input
                      className={`text-input ${styles.metaControl} ${styles.labelInput}`}
                      value={labelDraft}
                      onChange={event => {
                        setLabelDraft(event.target.value);
                        setLabelSuggestionsOpen(true);
                      }}
                      onFocus={() => setLabelSuggestionsOpen(true)}
                      onKeyDown={event => {
                        if (event.key === 'Enter' || event.key === ',') {
                          event.preventDefault();
                          addLabel(labelDraft);
                        }

                        if (event.key === 'Escape') {
                          setLabelSuggestionsOpen(false);
                        }
                      }}
                      onBlur={() => {
                        window.setTimeout(() => {
                          addLabel(labelDraft);
                          setLabelSuggestionsOpen(false);
                        }, 120);
                      }}
                      placeholder={t('labelsPlaceholder', { ns: 'tasks', defaultValue: 'Type a label and press Enter' })}
                    />

                    {labelSuggestionsOpen && suggestedLabels.length > 0 ? (
                      <div className={styles.labelSuggestions} role="listbox" aria-label={t('labelsLabel', { ns: 'tasks', defaultValue: 'Labels' })}>
                        {suggestedLabels.map(label => (
                          <button
                            key={label.id}
                            type="button"
                            className={styles.labelSuggestion}
                            onMouseDown={event => {
                              event.preventDefault();
                              addLabel(label.name);
                            }}
                          >
                            {label.name}
                          </button>
                        ))}
                      </div>
                    ) : null}
                </div>
              </div>
            </div>

            {isEdit ? (
              <div className={styles.sideCard}>
                <div className={styles.sideCardTitle}>{t('linkedConversationsTitle', { ns: 'tasks', defaultValue: 'Linked conversations' })}</div>

                {conversationLinks.length === 0 ? (
                  <div className={styles.sideCardEmpty}>{t('noLinkedConversations', { ns: 'tasks', defaultValue: 'This task is not linked to any conversations yet.' })}</div>
                ) : (
                  <div className={styles.conversationLinkList}>
                    {conversationLinks.map(link => {
                      const linkedByName = employeeById[link.linkedByEmployeeId]?.name || t('unknownAssignee', { ns: 'tasks' });
                      const isUnlinking = unlinkingConversationIds.includes(link.conversationId);
                      const conversationName = link.conversationTitle?.trim()
                        || t('conversationLabel', {
                          ns: 'tasks',
                          conversationId: link.conversationId.slice(0, 8),
                          defaultValue: `Conversation ${link.conversationId.slice(0, 8)}`,
                        });

                      return (
                        <div key={link.conversationId} className={styles.conversationLinkCard}>
                          <div className={styles.conversationLinkHeader}>
                            <button
                              type="button"
                              className={styles.conversationLinkButton}
                              title={conversationName}
                              onClick={() => handleOpenConversation(link.conversationId)}
                            >
                              {conversationName}
                            </button>

                            {link.isPrimary ? <span className={styles.primaryConversationBadge}>{t('primaryConversation', { ns: 'tasks', defaultValue: 'Primary' })}</span> : null}
                          </div>

                          <div className={styles.conversationLinkMeta}>
                            {t('conversationLinkMeta', {
                              ns: 'tasks',
                              linkedBy: linkedByName,
                              linkedAt: formatConversationLinkDate(link.linkedAt),
                              defaultValue: `Linked by ${linkedByName} on ${formatConversationLinkDate(link.linkedAt)}`,
                            })}
                          </div>

                          {link.conversationMessageId ? (
                            <div className={styles.conversationLinkMessageMeta}>
                              {t('linkedMessageLabel', {
                                ns: 'tasks',
                                messageId: link.conversationMessageId.slice(0, 8),
                                defaultValue: `From message ${link.conversationMessageId.slice(0, 8)}`,
                              })}
                            </div>
                          ) : null}

                          <div className={styles.conversationLinkActions}>
                            <button
                              type="button"
                              className="btn btn-secondary"
                              onClick={() => handleOpenConversation(link.conversationId)}
                            >
                              {t('openConversation', { ns: 'tasks', defaultValue: 'Open conversation' })}
                            </button>
                            <button
                              type="button"
                              className="btn btn-secondary"
                              onClick={() => void handleUnlinkConversation(link.conversationId)}
                              disabled={isUnlinking}
                            >
                              {isUnlinking ? t('saving', { ns: 'common' }) : t('unlinkConversation', { ns: 'tasks', defaultValue: 'Unlink' })}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : null}

            {isEdit ? (
              <div className={styles.sideCard}>
                <div className={styles.sideCardTitle}>{t('relatedTasksTitle', { ns: 'tasks', defaultValue: 'Related tasks' })}</div>

                <div className={styles.metaRows}>
                  <label className={styles.metaRow}>
                    <span className={styles.metaLabel}>{t('searchRelatedTasksLabel', { ns: 'tasks', defaultValue: 'Find task' })}</span>
                    <input
                      className={`text-input ${styles.metaControl}`}
                      value={relatedTaskSearch}
                      onChange={event => {
                        const nextSearchText = event.target.value;
                        setRelatedTaskSearch(nextSearchText);

                        if (relatedTaskSearchTimeoutRef.current !== null) {
                          window.clearTimeout(relatedTaskSearchTimeoutRef.current);
                        }

                        relatedTaskSearchTimeoutRef.current = window.setTimeout(() => {
                          void loadRelatedTaskCandidates(nextSearchText);
                          relatedTaskSearchTimeoutRef.current = null;
                        }, 250);
                      }}
                      placeholder={t('searchRelatedTasksPlaceholder', { ns: 'tasks', defaultValue: 'Type task title or key...' })}
                    />
                  </label>
                </div>

                {loadingRelatedTaskCandidates ? (
                  <div className={styles.sideCardEmpty}>{t('loading', { ns: 'tasks' })}</div>
                ) : null}

                {!loadingRelatedTaskCandidates && relatedTaskSearch.trim().length >= 2 && relatedTaskCandidates.length === 0 ? (
                  <div className={styles.sideCardEmpty}>{t('noRelatedTaskCandidates', { ns: 'tasks', defaultValue: 'No matching tasks found.' })}</div>
                ) : null}

                {relatedTaskCandidates.length > 0 ? (
                  <div className={styles.conversationLinkList}>
                    {relatedTaskCandidates.map(candidate => (
                      <div key={candidate.id} className={styles.conversationLinkCard}>
                        <div className={styles.conversationLinkHeader}>
                          <button type="button" className={styles.conversationLinkButton} onClick={() => handleOpenRelatedTask(candidate.id)}>
                            {candidate.taskKey || candidate.title}
                          </button>
                        </div>
                        <div className={styles.conversationLinkMeta}>{candidate.title}</div>
                        <div className={styles.conversationLinkActions}>
                          <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => void handleLinkRelatedTask(candidate.id)}
                            disabled={linkingRelatedTaskId === candidate.id}
                          >
                            {linkingRelatedTaskId === candidate.id
                              ? t('saving', { ns: 'common' })
                              : t('linkRelatedTask', { ns: 'tasks', defaultValue: 'Link task' })}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : null}

                {relatedTasks.length === 0 ? (
                  <div className={styles.sideCardEmpty}>{t('noRelatedTasks', { ns: 'tasks', defaultValue: 'This task has no related tasks yet.' })}</div>
                ) : (
                  <div className={styles.conversationLinkList}>
                    {relatedTasks.map(link => {
                      const linkedByName = employeeById[link.linkedByEmployeeId]?.name || t('unknownAssignee', { ns: 'tasks' });
                      const isUnlinking = unlinkingRelatedTaskIds.includes(link.relatedTaskId);

                      return (
                        <div key={link.relatedTaskId} className={styles.conversationLinkCard}>
                          <div className={styles.conversationLinkHeader}>
                            <button
                              type="button"
                              className={styles.conversationLinkButton}
                              onClick={() => handleOpenRelatedTask(link.relatedTaskId)}
                            >
                              {link.relatedTaskKey || link.relatedTaskTitle}
                            </button>
                            <span className={styles.primaryConversationBadge}>
                              {getTaskStatusLabel(link.relatedTaskStatus, statusOptions)}
                            </span>
                          </div>

                          <div className={styles.conversationLinkMeta}>{link.relatedTaskTitle}</div>
                          <div className={styles.conversationLinkMeta}>
                            {t('relatedTaskLinkMeta', {
                              ns: 'tasks',
                              linkedBy: linkedByName,
                              linkedAt: formatConversationLinkDate(link.linkedAt),
                              defaultValue: `Linked by ${linkedByName} on ${formatConversationLinkDate(link.linkedAt)}`,
                            })}
                          </div>

                          <div className={styles.conversationLinkActions}>
                            <button
                              type="button"
                              className="btn btn-secondary"
                              onClick={() => handleOpenRelatedTask(link.relatedTaskId)}
                            >
                              {t('openTask', { ns: 'tasks', defaultValue: 'Open task' })}
                            </button>
                            <button
                              type="button"
                              className="btn btn-secondary"
                              onClick={() => void handleUnlinkRelatedTask(link.relatedTaskId)}
                              disabled={isUnlinking}
                            >
                              {isUnlinking ? t('saving', { ns: 'common' }) : t('unlinkRelatedTask', { ns: 'tasks', defaultValue: 'Unlink' })}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </div>

        </div>

        <div className={`${styles.actions} ${isEmbedded ? styles.actionsEmbedded : ''}`}>
          {isEmbedded ? (
            <button className="btn btn-secondary" type="button" onClick={handleCancel} disabled={saving}>
              {t('cancel', { ns: 'common' })}
            </button>
          ) : null}

          {isEmbedded ? (
            <button className={`btn btn-primary ${actionIconStyles.buttonWithIcon}`} type="submit" disabled={saving}>
              <ButtonWithIcon icon={<SaveIcon />}>
                {saving ? t('saving', { ns: 'common' }) : t('save', { ns: 'tasks' })}
              </ButtonWithIcon>
            </button>
          ) : null}
        </div>
      </form>

      {lightboxAttachment ? ReactDOM.createPortal(
        <div
          className={styles.lightboxOverlay}
          onClick={() => setLightboxAttachment(null)}
          role="dialog"
          aria-modal="true"
          aria-label={lightboxAttachment.fileName}
        >
          <button
            type="button"
            className={styles.lightboxClose}
            onClick={() => setLightboxAttachment(null)}
            aria-label={t('close', { ns: 'common' })}
          >
            &times;
          </button>
          <div
            className={styles.lightboxContent}
            onClick={e => e.stopPropagation()}
          >
            {lightboxAttachment.mimeType.toLowerCase().startsWith('image/') ? (
              <img
                className={styles.lightboxImage}
                src={lightboxAttachment.mediaUrl}
                alt={lightboxAttachment.fileName}
              />
            ) : lightboxAttachment.mimeType.toLowerCase().startsWith('audio/') ? (
              <audio
                className={styles.lightboxMedia}
                controls
                autoPlay
                src={lightboxAttachment.mediaUrl}
              />
            ) : (
              <video
                className={styles.lightboxMedia}
                controls
                autoPlay
                src={lightboxAttachment.mediaUrl}
              />
            )}
            <div className={styles.lightboxFileName}>{lightboxAttachment.fileName}</div>
          </div>
        </div>,
        document.body
      ) : null}
    </div>
  );
};

export default TaskEditor;
